import {
  Button,
  CustomFilter,
  EllipsisSpan,
  Form,
  Input,
  Modal,
  Paging,
  Select,
  Table,
  useAsync,
  usePaging,
} from '@maxtropy/components';
import React, { FC, Key, useEffect, useState } from 'react';
import { useRequest } from 'ahooks';
import {
  apiV2DispatchUetStructDevicePagePost,
  apiV2DispatchUetStructMcidByUetIdsPost,
  apiV2DispatchUetStructOuByUetIdsPost,
  apiV2UetListUetTopologyStructPost,
  V2DispatchUetStructDevicePagePostRequest,
  V2DispatchUetStructDevicePagePostResponse,
} from '@maxtropy/intelligent-dispath-apis-v2';
import { Cascader } from 'antd';
import { getIdsDeviceTypeTree } from '@/api/device';
import { formatOptionData, getChildNodesByParentIds } from '@/components/CascadingMultipleSelector/utils';
import { DefaultOptionType } from 'antd/es/cascader';

const modalColumns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所在组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '运营单元',
    dataIndex: 'ouNames',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

interface AddMetersModalProps {
  topologyId: string | null;
  open: boolean;
  selectedRowKeys: Key[];
  onCancel: () => void;
  setSelectedRowKeys: (val: Key[]) => void;
  setSelectRows: (val: any[]) => void;
  gridAccessPointId?: React.Key;
}

export interface SearchParams extends Omit<V2DispatchUetStructDevicePagePostRequest, 'page' | 'size' | 'timestamp'> {}

// 电表 pcs 逆变器 电池堆 充电枪 充电桩等
// const DEVICE_TYPE = [5022, 8888, 5002, 5000, 5009, 5016, 5005, 5043, 5042, 5018];

const AddMetersModal: FC<AddMetersModalProps> = ({
  topologyId,
  open,
  selectedRowKeys,
  setSelectedRowKeys,
  onCancel,
  setSelectRows,
  gridAccessPointId,
}) => {
  const [form] = Form.useForm();
  const [uetIdsList, setUetIdsList] = useState<number[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const [searchParams, setSearchParams] = useState<SearchParams>();
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  const [metersDataSource, setMetersDataSource] = useState<V2DispatchUetStructDevicePagePostResponse['list']>([]); // 所有电表

  const deviceTypeData = useAsync(getIdsDeviceTypeTree);

  useEffect(() => {
    setLoading(true);
    if (!open || !uetIdsList || !gridAccessPointId || !topologyId) return;
    const deviceTypeIdList = getChildNodesByParentIds(
      searchParams?.deviceTypeIdList as unknown as number[][],
      deviceTypeData?.tree
    );
    apiV2DispatchUetStructDevicePagePost({
      ...searchParams,
      uetIds: uetIdsList,
      deviceTypeIdList: deviceTypeIdList,
      page: pageOffset,
      size: pageSize,
      uetStructId: +topologyId!,
      gridAccessPointId,
    })
      .then(res => {
        if (res) {
          setTotalCount(res.total ?? 0);
          setMetersDataSource(res.list ?? []);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, pageOffset, pageSize, searchParams, uetIdsList, setTotalCount, gridAccessPointId, topologyId]);

  // uetList
  useEffect(() => {
    if (!open) return;
    if (topologyId) {
      apiV2UetListUetTopologyStructPost({ id: +topologyId! }).then(res => {
        const UETIds = (res?.list?.map(item => item.id).filter(i => i !== undefined) as number[]) ?? [];
        setUetIdsList(UETIds);
      });
    }
  }, [open, topologyId]);

  // ou列表List
  const { data: ouList = [] } = useRequest(
    async () => {
      const res = await apiV2DispatchUetStructOuByUetIdsPost({
        ids: uetIdsList as number[],
      });
      if (res) {
        return res.list?.map(item => ({
          label: item.name,
          value: item.id,
        }));
      }
    },
    {
      ready: !!uetIdsList,
      refreshDeps: [uetIdsList],
    }
  );

  // 组织列表list
  const { data: treeData = [] } = useRequest(
    async () => {
      const res = await apiV2DispatchUetStructMcidByUetIdsPost({
        ids: uetIdsList as number[],
      });
      if (res) {
        return res.list?.map(item => ({
          label: item.name,
          value: item.mcid,
        }));
      }
    },
    {
      ready: !!uetIdsList,
      refreshDeps: [uetIdsList],
    }
  );

  const onFinish = (v: SearchParams) => {
    setSearchParams(v);
    setPageOffset(1);
  };

  const onReset = () => {
    setSearchParams(undefined);
    setPageOffset(1);
  };

  const onOk = () => {
    const selectedRow = (metersDataSource ?? []).filter(item => selectedRowKeys.includes(item.id as Key));
    setSelectRows(selectedRow);
    form.resetFields();
    onCancel();
  };

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

  const filters = (
    <CustomFilter form={form} colSpan={8} onFinish={onFinish} onReset={onReset}>
      <Form.Item name="codeOrName" label="编号/名称">
        <Input placeholder="请输入编号查询" />
      </Form.Item>

      <Form.Item name="mcid" label="所属组织">
        <Select options={treeData} placeholder="请选择" style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name="ouId" label="运营单元">
        <Select options={ouList} placeholder="请选择" style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name="deviceTypeIdList" label="所属类目">
        <Cascader
          options={formatOptionData(deviceTypeData)}
          allowClear={false}
          fieldNames={{ children: 'child' }}
          multiple
          maxTagCount="responsive"
          showSearch={{ filter }}
          placeholder={'请选择所属类目'}
        />
      </Form.Item>
    </CustomFilter>
  );

  const rowSelection = {
    onChange: (rowKeys: Key[], _rows: V2DispatchUetStructDevicePagePostResponse['list']) => {
      setSelectedRowKeys(rowKeys);
    },
    selectedRowKeys,
  };

  return (
    <>
      <Modal size="big" open={open} title="设备选择" onOk={onOk} onCancel={onCancel} destroyOnClose>
        {filters}
        <div style={{ float: 'right' }}>
          点击前往
          <Button
            type="link"
            onClick={() => window.open(`${window.IOTPLATFORMORIGIN}/device/manage/device/create`, '_blank')}
          >
            添加设备
          </Button>
        </div>

        <Table
          loading={loading}
          rowSelection={{
            type: 'radio',
            ...rowSelection,
          }}
          scroll={{ y: 300 }}
          rowKey="id"
          columns={modalColumns}
          dataSource={metersDataSource}
        />
        <Paging pagingInfo={pagingInfo} />
      </Modal>
    </>
  );
};

export default AddMetersModal;
